"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.databaseAttachmentFilterFunctions = exports.databaseViewFilterFunctions = void 0;
exports.isOlderThanDays = isOlderThanDays;
exports.getDatabaseViewFilterFunction = getDatabaseViewFilterFunction;
exports.matchesDatabaseViewFilter = matchesDatabaseViewFilter;
const file_index_1 = require("./file-index");
function isOlderThanDays(value, days, nowMs) {
    if (!Number.isSafeInteger(days) || days <= 0 || !Number.isFinite(nowMs)) {
        throw new Error('Cleanup age requires positive whole days and a valid reference time');
    }
    return (typeof value === 'number' &&
        Number.isFinite(value) &&
        value < nowMs - days * 86400000);
}
const text = (value) => String(value ?? '').toLowerCase();
const values = (value) => Array.isArray(value) ? value : [];
const empty = (value) => value == null || value === '' || (Array.isArray(value) && !value.length);
/** Pure operators shared by Data-View filter-fn UI definitions and server queries.
 * Select values are option IDs, exactly as rawValue.toJson supplies them.
 */
exports.databaseViewFilterFunctions = {
    olderThanDays: (self, days, nowMs) => isOlderThanDays(self, days, nowMs ?? Date.now()),
    isChecked: self => !!self,
    isUnchecked: self => !self,
    before: (self, target) => self != null && self < target,
    after: (self, target) => self != null && self > target,
    equal: (self, target) => self != null && Number(self) === Number(target),
    notEqual: (self, target) => self != null && Number(self) !== Number(target),
    greatThan: (self, target) => self != null && self > target,
    lessThan: (self, target) => self != null && self < target,
    greatThanOrEqual: (self, target) => self != null && self >= target,
    lessThanOrEqual: (self, target) => self != null && self <= target,
    contains: (self, target) => text(self).includes(text(target)),
    doesNoContains: (self, target) => !text(self).includes(text(target)),
    startsWith: (self, target) => text(self).startsWith(text(target)),
    endsWith: (self, target) => text(self).endsWith(text(target)),
    is: (self, target) => text(self) === text(target),
    isNot: (self, target) => text(self) !== text(target),
    isOneOf: (self, target) => !values(target).length || (self != null && values(target).includes(self)),
    isNotOneOf: (self, target) => !values(target).length || self == null || !values(target).includes(self),
    containsOneOf: (self, target) => !values(target).length ||
        (self != null &&
            values(target).some(value => values(self).includes(value))),
    doesNotContainOneOf: (self, target) => !values(target).length ||
        self == null ||
        values(target).every(value => !values(self).includes(value)),
    containsAll: (self, target) => !values(target).length ||
        (self != null &&
            values(target).every(value => values(self).includes(value))),
    doesNotContainAll: (self, target) => !values(target).length ||
        self == null ||
        !values(target).every(value => values(self).includes(value)),
    isEmpty: empty,
    isNotEmpty: self => !empty(self),
};
exports.databaseAttachmentFilterFunctions = {
    isEmpty: self => (0, file_index_1.normalizeAttachmentCell)(self).isEmpty,
    isNotEmpty: self => !(0, file_index_1.normalizeAttachmentCell)(self).isEmpty,
    filenameContains: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).filenames.some(name => text(name).includes(text(target))),
    mimeStartsWith: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).mimeTypes.some(mime => text(mime).startsWith(text(target))),
    countEqual: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).count === target,
    countNotEqual: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).count !== target,
    countGreaterThan: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).count > target,
    countGreaterThanOrEqual: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).count >= target,
    countLessThan: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).count < target,
    countLessThanOrEqual: (self, target) => (0, file_index_1.normalizeAttachmentCell)(self).count <= target,
};
function getDatabaseViewFilterFunction(name, columnType) {
    const functions = columnType === 'attachment' ||
        (Object.prototype.hasOwnProperty.call(exports.databaseAttachmentFilterFunctions, name) &&
            !Object.prototype.hasOwnProperty.call(exports.databaseViewFilterFunctions, name))
        ? exports.databaseAttachmentFilterFunctions
        : exports.databaseViewFilterFunctions;
    return Object.prototype.hasOwnProperty.call(functions, name)
        ? functions[name]
        : undefined;
}
function matchesDatabaseViewFilter(filter, readValue, readColumnType, context = { nowMs: Date.now() }) {
    if (filter.type === 'group') {
        if (!filter.conditions.length)
            return true;
        const evaluate = (child) => matchesDatabaseViewFilter(child, readValue, readColumnType, context);
        return filter.op === 'and'
            ? filter.conditions.every(evaluate)
            : filter.conditions.some(evaluate);
    }
    const fn = getDatabaseViewFilterFunction(filter.function, readColumnType(filter.left.name));
    if (!fn)
        throw new Error('Unsupported database view filter operator');
    if (filter.function === 'olderThanDays') {
        return isOlderThanDays(readValue(filter.left.name), filter.args[0]?.value, context.nowMs);
    }
    return fn(readValue(filter.left.name), ...filter.args.map(arg => arg.value));
}
